import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Alert, IconButton, CircularProgress,
    Card, CardContent, Collapse, Stack, Divider, Tooltip, Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Sync as SyncIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    AccountTree as AccountTreeIcon,
    Warning as WarningIcon,
    TableChart as TableChartIcon,
    FilterList as FilterListIcon,
    Info as InfoIcon
} from '@mui/icons-material';

// API definitions
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const api = {
    get: (endpoint) => axios.get(`${API_URL}${endpoint}`),
    post: (endpoint, data) => axios.post(`${API_URL}${endpoint}`, data),
};
const getConfig = (key) => api.get(`/config/${key}`);
const saveConfig = (key, data) => api.post(`/config/${key}`, data);
const reprocessarUltimoMes = () => api.post('/reprocessar_ultimo_mes');

const ALLOCATION_RULES_KEY = 'alocacao_custos';
const ALLOCATION_TYPES_KEY = 'allocation_types_config';
const METODO_RATEIO_KEY = 'metodo_rateio_config';

const AlocacaoCustos = () => {
    const [regras, setRegras] = useState([]);
    const [ccOptions, setCCOptions] = useState([]);
    const [contaOptions, setContaOptions] = useState([]);
    const [allAllocationTypes, setAllAllocationTypes] = useState([]);
    const [auxiliaryTables, setAuxiliaryTables] = useState([]);
    const [metodoRateio, setMetodoRateio] = useState('sequencial');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [expandedRule, setExpandedRule] = useState(null);

    const PADRAO_TYPES = [
        { id: 'direto', name: 'Alocação Direta', isStandard: true },
        { id: 'step_down', name: 'Step-Down (Sequencial)', isStandard: true },
        { id: 'receita_total', name: 'Receita Total', isStandard: true },
        { id: 'custo_total', name: 'Custo Total', isStandard: true },
        { id: 'producao', name: 'Produção (Ponderada por Pesos)', isStandard: true },
        { id: 'producao_volume', name: 'Volume (Quantidade Bruta)', isStandard: true },
        { id: 'receita_direta', name: 'Receita Direta', isStandard: true },
        { id: 'porcentagem_fixa', name: 'Porcentagem Fixa', isStandard: true },
        { id: 'tabela_auxiliar', name: 'Tabela Auxiliar', isStandard: true, description: 'Distribui custos proporcionalmente aos valores de uma tabela auxiliar (ex: funcionários, m², leitos)' },
    ];
    const MANUAL_DESTINATION_TYPES = ['porcentagem_fixa', 'step_down', 'producao', 'producao_volume', 'receita_direta'];
    // eslint-disable-next-line no-unused-vars
    let AUTOMATIC_DESTINATION_TYPES = ['receita_total', 'custo_total', 'producao', 'producao_volume', 'receita_direta', 'tabela_auxiliar'];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        try {
            const regrasRes = await getConfig(ALLOCATION_RULES_KEY);
            const loadedRegras = Array.isArray(regrasRes.data)
                ? regrasRes.data.map((regra, idx) => ({
                    ...regra,
                    id: regra.id || Date.now() + idx,
                    origem_tipo: regra.origem_tipo || 'Centro de Custo',
                    ratear_o_que: regra.ratear_o_que || 'Despesa',
                    tipo_alocacao: regra.tipo_alocacao || 'porcentagem_fixa',
                    distribuir_todos: regra.distribuir_todos !== undefined ? regra.distribuir_todos : (regra.tipo_alocacao === 'producao'),
                    filtros_direcionamento: regra.filtros_direcionamento || [],
                    driver: regra.driver || '',
                    destinos: (regra.destinos && regra.destinos.length > 0) ? regra.destinos : [{ nome: '', porcentagem: 100 }],
                }))
                : [];
            setRegras(loadedRegras);

            const typesRes = await getConfig(ALLOCATION_TYPES_KEY);
            const dynamicTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
            const dynamicTypeIds = dynamicTypes.map(t => t.id).filter(id => id);
            // eslint-disable-next-line no-unused-vars
            AUTOMATIC_DESTINATION_TYPES = ['receita_total', 'custo_total', 'producao', 'tabela_auxiliar', ...dynamicTypeIds];
            setAllAllocationTypes([...PADRAO_TYPES, ...dynamicTypes]);

            const ccRes = await getConfig('centros_custo');
            setCCOptions((ccRes.data || []).map(cc => cc.nome).sort());

            const pcRes = await getConfig('plano_contas_data');
            const planoContas = pcRes.data || [];
            const contasArr = planoContas.map(pc => ({
                id: pc.Conta,
                label: pc.Descricao ? `${pc.Conta} - ${pc.Descricao}` : pc.Conta
            }));

            // Deduplicar por id (Conta)
            const uniqueContas = [];
            const idsVistos = new Set();
            contasArr.forEach(c => {
                if (!idsVistos.has(c.id)) {
                    idsVistos.add(c.id);
                    uniqueContas.push(c);
                }
            });

            setContaOptions([
                { id: 'TODAS - Despesa', label: 'TODAS - Despesa' },
                { id: 'TODAS - Receita', label: 'TODAS - Receita' },
                ...uniqueContas.sort((a, b) => a.id.localeCompare(b.id))
            ]);

            // Load auxiliary tables
            const auxRes = await getConfig('dynamic_bases_config');
            setAuxiliaryTables(Array.isArray(auxRes.data) ? auxRes.data : []);

            const metodoRes = await getConfig(METODO_RATEIO_KEY);
            if (metodoRes.data && metodoRes.data.metodo) setMetodoRateio(metodoRes.data.metodo);
        } catch (err) {
            console.error('Erro ao carregar dados:', err);
            setError('Erro ao carregar configurações. Verifique o console do servidor.');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchAllData();
    }, []);

    const requiresManualDestinations = (tipo) => MANUAL_DESTINATION_TYPES.includes(tipo);
    const getCurrentOptions = (origemTipo) => (origemTipo === 'Conta Contábil' ? contaOptions : ccOptions);

    const handleAddRule = () => {
        const newRule = {
            id: Date.now(),
            origem_tipo: 'Centro de Custo',
            ratear_o_que: 'Despesa',
            origem: ccOptions.length > 0 ? ccOptions[0] : '',
            tipo_alocacao: 'porcentagem_fixa',
            distribuir_todos: false,
            filtros_direcionamento: [],
            driver: '',
            destinos: [{ nome: ccOptions.length > 0 ? ccOptions[0] : '', porcentagem: 100 }],
        };
        setRegras([...regras, newRule]);
        setExpandedRule(newRule.id);
    };

    const handleRuleChange = (id, field, value) => {
        setRegras(regras.map(regra => {
            if (regra.id !== id) return regra;
            const updated = { ...regra, [field]: value };
            if (field === 'origem_tipo') {
                const opts = getCurrentOptions(value);
                updated.origem = opts.length > 0 ? (typeof opts[0] === 'string' ? opts[0] : opts[0].id) : '';
            } else if (field === 'tipo_alocacao') {
                const manual = MANUAL_DESTINATION_TYPES.includes(value);
                if (!manual) updated.destinos = [];
                else if (value === 'porcentagem_fixa' && (!updated.destinos || updated.destinos.length === 0)) {
                    updated.destinos = [{ nome: ccOptions.length > 0 ? ccOptions[0] : '', porcentagem: 100 }];
                } else if (value === 'porcentagem_fixa') {
                    const n = updated.destinos.length || 1;
                    const pct = Math.round((100 / n) * 100) / 100;
                    updated.destinos = updated.destinos.map(d => ({ ...d, porcentagem: pct }));
                } else if (value === 'step_down' && updated.destinos.length === 0) {
                    updated.destinos = [{ nome: ccOptions.length > 0 ? ccOptions[0] : '', porcentagem: 0 }];
                }

                if (['producao', 'producao_volume', 'receita_direta'].includes(value)) {
                    updated.distribuir_todos = true;
                    updated.destinos = [];
                } else {
                    updated.distribuir_todos = false;
                }

                // Set default driver for tabela_auxiliar
                if (value === 'tabela_auxiliar' && auxiliaryTables.length > 0 && !updated.driver) {
                    updated.driver = auxiliaryTables[0].id;
                }
            }
            return updated;
        }));
    };

    const handleDestinationChange = (ruleId, idx, field, value) => {
        setRegras(regras.map(regra => {
            if (regra.id !== ruleId) return regra;
            const newDest = [...regra.destinos];
            newDest[idx] = { ...newDest[idx], [field]: field === 'porcentagem' ? parseFloat(value) || 0 : value };
            if (field === 'porcentagem' && regra.tipo_alocacao === 'porcentagem_fixa' && newDest.length === 1) {
                newDest[idx].porcentagem = 100;
            }
            return { ...regra, destinos: newDest };
        }));
    };

    const handleAddDestination = (ruleId) => {
        setRegras(regras.map(regra => {
            if (regra.id !== ruleId) return regra;
            const novo = { nome: ccOptions.length > 0 ? ccOptions[0] : '', porcentagem: 0 };
            const newDest = [...regra.destinos, novo];
            if (regra.tipo_alocacao === 'porcentagem_fixa') {
                const n = newDest.length;
                const pct = Math.round((100 / n) * 100) / 100;
                return { ...regra, destinos: newDest.map(d => ({ ...d, porcentagem: pct })) };
            }
            return { ...regra, destinos: newDest };
        }));
    };

    const handleRemoveDestination = (ruleId, idx) => {
        setRegras(regras.map(regra => {
            if (regra.id !== ruleId) return regra;
            const filtered = regra.destinos.filter((_, i) => i !== idx);
            if (regra.tipo_alocacao === 'porcentagem_fixa') {
                if (filtered.length === 1) filtered[0].porcentagem = 100;
                else if (filtered.length > 1) {
                    const n = filtered.length;
                    const pct = Math.round((100 / n) * 100) / 100;
                    filtered.forEach(d => d.porcentagem = pct);
                }
            }
            return { ...regra, destinos: filtered };
        }));
    };

    const handleAddFilter = (ruleId) => {
        setRegras(regras.map(regra => {
            if (regra.id !== ruleId) return regra;
            const newFilters = [...(regra.filtros_direcionamento || []), { campo: '', valor: '' }];
            return { ...regra, filtros_direcionamento: newFilters };
        }));
    };

    const handleFilterChange = (ruleId, idx, field, value) => {
        setRegras(regras.map(regra => {
            if (regra.id !== ruleId) return regra;
            const newFilters = [...regra.filtros_direcionamento];
            newFilters[idx] = { ...newFilters[idx], [field]: value };
            return { ...regra, filtros_direcionamento: newFilters };
        }));
    };

    const handleRemoveFilter = (ruleId, idx) => {
        setRegras(regras.map(regra => {
            if (regra.id !== ruleId) return regra;
            const newFilters = regra.filtros_direcionamento.filter((_, i) => i !== idx);
            return { ...regra, filtros_direcionamento: newFilters };
        }));
    };

    const handleRemoveRule = (id) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('Tem certeza que deseja remover esta Regra de Rateio?')) return;
        setRegras(regras.filter(r => r.id !== id));
        if (expandedRule === id) setExpandedRule(null);
    };

    const validateAndSave = async (callback) => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        const errors = [];
        regras.forEach((regra, i) => {
            const label = `Regra #${i + 1} (${regra.origem_tipo}: ${regra.origem || 'Vazio'})`;
            const manual = requiresManualDestinations(regra.tipo_alocacao);
            if (regra.tipo_alocacao === 'porcentagem_fixa' && regra.destinos.length > 0) {
                const total = regra.destinos.reduce((s, d) => s + (d.porcentagem || 0), 0);
                if (Math.abs(total - 100) > 0.01) errors.push(`${label}: total % deve ser 100. Atual ${total.toFixed(2)}%`);
            }
            if (!regra.origem) errors.push(`${label}: Origem não pode ser vazia.`);
            if (!regra.tipo_alocacao) errors.push(`${label}: Tipo de alocação obrigatório.`);
            if (regra.tipo_alocacao === 'tabela_auxiliar' && !regra.driver) {
                errors.push(`${label}: Selecione uma tabela auxiliar.`);
            }

            if (manual) {
                if (regra.tipo_alocacao === 'producao' && regra.distribuir_todos) {
                    // Skip destination validation if distributing to all
                } else {
                    regra.destinos.forEach((d, di) => {
                        if (!d.nome) errors.push(`${label} (Destino #${di + 1}): Nome vazio.`);
                    });
                    if (regra.tipo_alocacao !== 'direto' && (!regra.destinos || regra.destinos.length === 0)) {
                        errors.push(`${label}: Deve haver ao menos um destino.`);
                    }
                }
            }
        });
        if (errors.length) {
            setError(errors.join(' | '));
            setSaving(false);
            const errRule = regras.find((_, i) => errors.some(e => e.includes(`Regra #${i + 1}`)));
            if (errRule) setExpandedRule(errRule.id);
            return false;
        }
        try {
            const toSave = regras.map(({ id, ...rest }) => rest);
            await saveConfig(ALLOCATION_RULES_KEY, toSave);
            await saveConfig(METODO_RATEIO_KEY, { metodo: metodoRateio });
            setSuccess('Regras e método salvos com sucesso!');
            setSaving(false);
            if (callback) callback();
            return true;
        } catch (e) {
            setError('Erro ao salvar regras.');
            setSaving(false);
            return false;
        }
    };

    const handleSaveAndReprocess = async () => {
        setIsReprocessing(true);
        const ok = await validateAndSave();
        if (!ok) { setIsReprocessing(false); return; }
        setError(null);
        setSuccess('Regras salvas. Iniciando reprocessamento...');
        try {
            const cfg = await getConfig('ultimo_processamento');
            const mes = cfg.data?.mes || 'atual';
            const res = await reprocessarUltimoMes();
            if (res.data.status === 'Erro') {
                setError(res.data.mensagem || 'Falha ao reprocessar.');
                setSuccess(null);
            } else {
                setSuccess(`Mês ${mes} reprocessado com sucesso.`);
            }
        } catch (e) {
            setError(e.response?.data?.mensagem || 'Erro de conexão ao reprocessar.');
            setSuccess(null);
        } finally {
            setIsReprocessing(false);
            setSaving(false);
            setTimeout(() => setSuccess(null), 4000);
        }
    };

    const getAllocationTypeName = (id) => {
        const t = allAllocationTypes.find(t => t.id === id);
        return t ? t.name : id;
    };

    const getAuxiliaryTableName = (driverId) => {
        const table = auxiliaryTables.find(t => t.id === driverId);
        return table ? table.name : driverId;
    };

    const toggleRule = (id) => setExpandedRule(expandedRule === id ? null : id);

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <AccountTreeIcon sx={{ mr: 2, color: 'primary.main' }} /> Alocação de Custos
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Defina as regras para o rateio de custos e receitas de setores de apoio para os setores assistenciais.
            </Typography>

            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>Configuração Geral</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Método de Rateio</InputLabel>
                            <Select
                                value={metodoRateio}
                                label="Método de Rateio"
                                onChange={(e) => setMetodoRateio(e.target.value)}
                            >
                                <MenuItem value="sequencial">Sequencial (Step-Down)</MenuItem>
                                <MenuItem value="algebrico">Algébrico (Recíproco)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Typography variant="body2" color="text.secondary">
                            {metodoRateio === 'sequencial'
                                ? 'Distribui custos em cascata, sem reciprocidade total.'
                                : 'Resolve dependências circulares (ex: TI <-> Manutenção) simultaneamente.'}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Feedback */}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleAddRule}
                    startIcon={<AddIcon />}
                >
                    Adicionar Nova Regra
                </Button>
            </Box>

            <Stack spacing={2}>
                {regras.map((regra, idx) => (
                    <Card key={regra.id} variant="outlined" sx={{ overflow: 'visible' }}>
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                bgcolor: expandedRule === regra.id ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => toggleRule(regra.id)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold">Regra #{idx + 1}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {regra.origem_tipo === 'Conta Contábil'
                                        ? (contaOptions.find(o => o.id === regra.origem)?.label || regra.origem)
                                        : (regra.origem || 'Origem não definida')}
                                    <Box component="span" sx={{ mx: 1 }}>➔</Box>
                                    {getAllocationTypeName(regra.tipo_alocacao)}
                                    {regra.tipo_alocacao === 'tabela_auxiliar' && regra.driver && (
                                        <Box component="span" sx={{ ml: 1, color: 'primary.main' }}>
                                            ({getAuxiliaryTableName(regra.driver)})
                                        </Box>
                                    )}
                                    {regra.filtros_direcionamento && regra.filtros_direcionamento.length > 0 && (
                                        <Chip
                                            size="small"
                                            icon={<FilterListIcon />}
                                            label={`${regra.filtros_direcionamento.length} Filtros`}
                                            color="warning"
                                            variant="outlined"
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {expandedRule === regra.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                <Tooltip title="Remover Regra">
                                    <IconButton
                                        onClick={(e) => { e.stopPropagation(); handleRemoveRule(regra.id); }}
                                        color="error"
                                        size="small"
                                        sx={{ ml: 2 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Collapse in={expandedRule === regra.id}>
                            <CardContent>
                                <Grid container spacing={3} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Tipo de Origem</InputLabel>
                                            <Select
                                                value={regra.origem_tipo}
                                                label="Tipo de Origem"
                                                onChange={e => handleRuleChange(regra.id, 'origem_tipo', e.target.value)}
                                            >
                                                <MenuItem value="Centro de Custo">Centro de Custo</MenuItem>
                                                <MenuItem value="Conta Contábil">Conta Contábil</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Origem</InputLabel>
                                            <Select
                                                value={regra.origem}
                                                label="Origem"
                                                onChange={e => handleRuleChange(regra.id, 'origem', e.target.value)}
                                            >
                                                <MenuItem value=""><em>Selecione...</em></MenuItem>
                                                {getCurrentOptions(regra.origem_tipo).map(opt => {
                                                    const value = typeof opt === 'string' ? opt : opt.id;
                                                    const label = typeof opt === 'string' ? opt : opt.label;
                                                    return (
                                                        <MenuItem key={value} value={value}>
                                                            {label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>O que ratear?</InputLabel>
                                            <Select
                                                value={regra.ratear_o_que}
                                                label="O que ratear?"
                                                onChange={e => handleRuleChange(regra.id, 'ratear_o_que', e.target.value)}
                                            >
                                                <MenuItem value="Despesa">Despesa</MenuItem>
                                                <MenuItem value="Receita">Receita</MenuItem>
                                                <MenuItem value="Ambos">Ambos</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={3} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={regra.tipo_alocacao === 'tabela_auxiliar' ? 6 : 12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Tipo de Alocação</InputLabel>
                                            <Select
                                                value={regra.tipo_alocacao}
                                                label="Tipo de Alocação"
                                                onChange={e => handleRuleChange(regra.id, 'tipo_alocacao', e.target.value)}
                                            >
                                                {allAllocationTypes.map(t => (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>))}
                                            </Select>
                                        </FormControl>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            {allAllocationTypes.find(t => t.id === regra.tipo_alocacao)?.description}
                                        </Typography>
                                    </Grid>

                                    {regra.tipo_alocacao === 'tabela_auxiliar' && (
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Tabela Auxiliar (Driver)</InputLabel>
                                                <Select
                                                    value={regra.driver || ''}
                                                    label="Tabela Auxiliar (Driver)"
                                                    onChange={e => handleRuleChange(regra.id, 'driver', e.target.value)}
                                                    startAdornment={<TableChartIcon sx={{ mr: 1, color: 'primary.main' }} />}
                                                >
                                                    <MenuItem value=""><em>Selecione uma tabela...</em></MenuItem>
                                                    {auxiliaryTables.map(t => (
                                                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Os custos serão distribuídos proporcionalmente aos valores da tabela selecionada.
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>

                                {['producao', 'producao_volume', 'receita_direta'].includes(regra.tipo_alocacao) && (
                                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="checkbox"
                                            id={`distribuir-todos-${regra.id}`}
                                            checked={regra.distribuir_todos}
                                            onChange={e => handleRuleChange(regra.id, 'distribuir_todos', e.target.checked)}
                                            style={{ marginRight: '8px', width: '18px', height: '18px' }}
                                        />
                                        <label htmlFor={`distribuir-todos-${regra.id}`} style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                                            Distribuir para todos os centros com produção (Automático)
                                        </label>
                                    </Box>
                                )}

                                {['producao', 'producao_volume', 'receita_direta'].includes(regra.tipo_alocacao) && regra.distribuir_todos && (
                                    <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                                        O valor será distribuído proporcionalmente entre todos os centros que tiverem produção importada.
                                    </Alert>
                                )}

                                {/* --- NOVO: FILTROS DE DIRECIONAMENTO (RATEIO DIRECIONADO) --- */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderLeft: 4, borderColor: 'warning.main' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                            <FilterListIcon sx={{ mr: 1, color: 'warning.main', fontSize: 18 }} /> Filtros de Direcionamento (Opcional)
                                            <Tooltip title="Se definidos, esta regra será aplicada APENAS aos atendimentos que atendam a TODOS os critérios (AND logic). O valor será distribuído proporcionalmente ao volume/peso desses atendimentos.">
                                                <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'action.active' }} />
                                            </Tooltip>
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleAddFilter(regra.id)}
                                            color="warning"
                                        >
                                            Adicionar Filtro
                                        </Button>
                                    </Box>

                                    {regra.filtros_direcionamento && regra.filtros_direcionamento.length > 0 ? (
                                        <Stack spacing={1} sx={{ mt: 1 }}>
                                            {regra.filtros_direcionamento.map((filter, fIdx) => (
                                                <Box key={fIdx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <FormControl sx={{ minWidth: 200 }} size="small">
                                                        <InputLabel>Campo</InputLabel>
                                                        <Select
                                                            value={filter.campo}
                                                            label="Campo"
                                                            onChange={(e) => handleFilterChange(regra.id, fIdx, 'campo', e.target.value)}
                                                        >
                                                            <MenuItem value="convenio">Convênio</MenuItem>
                                                            <MenuItem value="especialidade">Especialidade</MenuItem>
                                                            <MenuItem value="medico_atendimento">Médico</MenuItem>
                                                            <MenuItem value="tipo_atendimento">Tipo Atendimento</MenuItem>
                                                            <MenuItem value="carater_atendimento">Caráter Atendimento</MenuItem>
                                                            <MenuItem value="tipo_internacao">Tipo Internação</MenuItem>
                                                            <MenuItem value="procedimento_principal">Procedimento Principal</MenuItem>
                                                            <MenuItem value="item">Item / Descrição</MenuItem>
                                                            <MenuItem value="codigo">Código</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <TextField
                                                        label="Valor Exato"
                                                        size="small"
                                                        fullWidth
                                                        value={filter.valor}
                                                        onChange={(e) => handleFilterChange(regra.id, fIdx, 'valor', e.target.value)}
                                                        placeholder="ex: SUS ou UNIMED"
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveFilter(regra.id, fIdx)}
                                                    >
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Nenhum filtro aplicado. Esta regra se aplica a todo o CC de destino ou base de cálculo.
                                        </Typography>
                                    )}
                                </Paper>

                                {requiresManualDestinations(regra.tipo_alocacao) && (!regra.distribuir_todos) && (
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {regra.tipo_alocacao === 'producao' ? 'Centros de Custo de Destino' : 'Destinos e Pesos'}
                                            </Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => handleAddDestination(regra.id)}
                                            >
                                                Adicionar Destino
                                            </Button>
                                        </Box>
                                        {['producao', 'producao_volume', 'receita_direta'].includes(regra.tipo_alocacao) && (
                                            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                                                Os pesos serão calculados automaticamente com base no volume (ou receita) de cada centro selecionado.
                                            </Alert>
                                        )}
                                        <Stack spacing={2}>
                                            {regra.destinos.map((dest, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Destino</InputLabel>
                                                        <Select
                                                            value={dest.nome}
                                                            label="Destino"
                                                            onChange={e => handleDestinationChange(regra.id, i, 'nome', e.target.value)}
                                                        >
                                                            <MenuItem value=""><em>Selecione o Destino...</em></MenuItem>
                                                            {ccOptions.map(cc => (<MenuItem key={cc} value={cc}>{cc}</MenuItem>))}
                                                        </Select>
                                                    </FormControl>
                                                    {regra.tipo_alocacao !== 'producao' && (
                                                        <TextField
                                                            type="number"
                                                            label="%"
                                                            value={dest.porcentagem}
                                                            onChange={e => handleDestinationChange(regra.id, i, 'porcentagem', e.target.value)}
                                                            size="small"
                                                            sx={{ width: 100 }}
                                                        />
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveDestination(regra.id, i)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}
                            </CardContent>
                        </Collapse>
                    </Card>
                ))}
            </Stack>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => validateAndSave()}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                    Salvar Regras
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handleSaveAndReprocess}
                    disabled={isReprocessing || saving}
                    startIcon={isReprocessing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                >
                    Salvar e Reprocessar
                </Button>
            </Box>
        </Box>
    );
};

export default AlocacaoCustos;